import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/video.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPlay, FaVideo, FaYoutube, FaTimes } from "react-icons/fa";
import ISF_API_BASE, { fetchAdminConfig, placeholderImage } from "./config";

// Recommended (YouTube) videos
const YT_TOPIC_KEY = 'skillmaster_yt_topic';
const YT_RESULTS = 12;


// Cache key for videos
const VIDEOS_CACHE_KEY = 'skillmaster_videos_cache';
const CACHE_EXPIRY_KEY = 'skillmaster_videos_cache_expiry';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Helper functions for caching
const getCachedVideos = () => {
  try {
    const cached = localStorage.getItem(VIDEOS_CACHE_KEY);
    const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
    
    if (cached && expiry && Date.now() < parseInt(expiry)) {
      console.log('✅ Using cached videos data');
      return JSON.parse(cached);
    }
    
    console.log('⚠️ Cache expired or not found');
    return null;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

const setCachedVideos = (data) => {
  try {
    localStorage.setItem(VIDEOS_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
    console.log('✅ Videos cached successfully');
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

const Video = () => {
  const [videos, setVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedPartner, setSelectedPartner] = useState("All");
  const [partnerMap, setPartnerMap] = useState({});
  const [categoryMap, setCategoryMap] = useState({});
  const [visibleCount, setVisibleCount] = useState(24);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  const savedInterests = JSON.parse(localStorage.getItem("selectedInterests")) || [];
  const userRole = localStorage.getItem("userRole");
  const isSuperAdmin = userRole === "superadmin" || savedInterests.includes("All");
  
  // Admin configurations
  const [adminCourseVisibility, setAdminCourseVisibility] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);
  
  // User data
  const userData = JSON.parse(localStorage.getItem("userData")) || { id: 96, reg_id: 5 };
  const mentorId = userData?.id || 96;

  // API Base URL
  const API_BASE = "https://k12.easylearn.org.in/Easylearn";

  // ==================== RECOMMENDED (YOUTUBE) VIDEOS ====================
  const getDefaultTopic = () => {
    const saved = localStorage.getItem(YT_TOPIC_KEY);
    if (saved) return saved;
    const interest = savedInterests.find((i) => i && i !== "All");
    return interest || "Skill development";
  };

  const [ytTopicInput, setYtTopicInput] = useState(getDefaultTopic);
  const [ytTopic, setYtTopic] = useState(getDefaultTopic);
  const [ytVideos, setYtVideos] = useState([]);
  const [ytLoading, setYtLoading] = useState(false);
  const [ytError, setYtError] = useState("");
  const [playingVideo, setPlayingVideo] = useState(null);

  const fetchRecommended = useCallback(async (topic) => {
    if (!topic || !topic.trim()) return;
    setYtLoading(true);
    setYtError("");
    try {
      const res = await fetch(
        `${ISF_API_BASE}/skillmaster/youtube/recommend?topic=${encodeURIComponent(topic.trim())}&max_results=${YT_RESULTS}`
      );
      const data = await res.json();
      if (data.success) {
        setYtVideos(data.videos || []);
        if (!data.videos || data.videos.length === 0) {
          setYtError(`No videos found for "${topic}"`);
        }
      } else {
        setYtVideos([]);
        setYtError(data.message || "Could not load recommended videos");
      }
    } catch (error) {
      console.error("❌ Error fetching recommended videos:", error);
      setYtVideos([]);
      setYtError("Could not load recommended videos");
    } finally {
      setYtLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecommended(ytTopic);
  }, [ytTopic, fetchRecommended]);

  const handleTopicSubmit = (e) => {
    e.preventDefault();
    const topic = ytTopicInput.trim();
    if (!topic) return;
    localStorage.setItem(YT_TOPIC_KEY, topic);
    if (topic === ytTopic) {
      fetchRecommended(topic);
    } else {
      setYtTopic(topic);
    }
  };

  // Close player on ESC + lock background scroll
  useEffect(() => {
    if (!playingVideo) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPlayingVideo(null);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [playingVideo]);
  // ==================== END RECOMMENDED (YOUTUBE) VIDEOS ====================

  // FIXED: Category detection - SPECIFIC categories pehle check karo, K12 last mein
  const getCategoryFromPartner = (partnerName, courseName = '') => {
    const partner = (partnerName || '').toLowerCase().trim();
    const course = (courseName || '').toLowerCase().trim();
    
    // Combined text for checking
    const combined = `${partner} ${course}`;
    
    // 1. UPSC & Competitive Exams (MOST SPECIFIC)
    if (/upsc|ias|ips|civil\s*service|competitive\s*exam/i.test(combined)) {
      return 'UPSC';
    }
    
    // 2. Medical (VERY SPECIFIC)
    if (/medical|neet|mbbs|nursing|aiims|jipmer/i.test(combined)) {
      return 'Medical';
    }
    
    // 3. Management (SPECIFIC)
    if (/\bmba\b|management|business\s*admin|pgdm/i.test(combined)) {
      return 'Management';
    }
    
    // 4. Technology (SPECIFIC)
    if (/\bit\b|software|programming|coding|computer\s*science|web\s*dev|app\s*dev|data\s*science|artificial\s*intelligence|machine\s*learning/i.test(combined)) {
      return 'Technology';
    }
    
    // 5. Vocational & Skills (SPECIFIC)
    if (/vocational|skill\s*development|training|apprentice|diploma|iti\b|polytechnic/i.test(combined)) {
      return 'Vocational';
    }
    
    // 6. Industry (SPECIFIC)
    if (/industry|manufacturing|production|factory|industrial/i.test(combined)) {
      return 'Industry';
    }
    
    // 7. Agriculture (SPECIFIC)
    if (/agriculture|farming|crop|agri|horticulture|animal\s*husbandry/i.test(combined)) {
      return 'Agriculture';
    }
    
    // 8. Leadership (SPECIFIC)
    if (/leadership|leader|executive|ceo|management\s*skills/i.test(combined)) {
      return 'Leadership';
    }
    
    // 9. Mathematics (MODERATELY SPECIFIC - but NOT if it's a class subject)
    // Only if "mathematics" is main subject, not part of "Class 6 Mathematics"
    if (/^math|^mathematics|advanced\s*math|higher\s*math|calculus|algebra|geometry|trigonometry/i.test(combined) && 
        !/class|grade|std|school/i.test(combined)) {
      return 'Mathematics';
    }
    
    // 10. K12 Education (LEAST SPECIFIC - check LAST)
    // Only assign K12 if it has clear school indicators
    if (/\bk12\b|k-12|aarshi|idream|class\s*\d+|grade\s*\d+|std\s*\d+|cbse|icse|state\s*board|school/i.test(combined)) {
      return 'K12';
    }
    
    // 11. If Mathematics subject appears in course name without K12 indicators
    if (/math|mathematics/i.test(combined)) {
      return 'Mathematics';
    }
    
    // Default: Other
    return 'Other';
  };

  // Load admin configuration
  useEffect(() => {
    const loadAdminConfig = async () => {
      try {
        const config = await fetchAdminConfig();
        if (config.courseVisibility) setAdminCourseVisibility(config.courseVisibility);
      } finally {
        setConfigLoaded(true);
      }
    };

    loadAdminConfig();
  }, []);

  // Check if category is visible
  const isCategoryVisible = (category) => {
    if (isSuperAdmin) return true;
    if (!configLoaded) return true;
    
    const visibility = adminCourseVisibility[category];
    return visibility === true || visibility === undefined;
  };

  // Extract thumbnail from video URL - Enhanced version
  const extractThumbnail = (videoUrl) => {
    if (!videoUrl) return placeholderImage("Video", 320, 180);
    
    try {
      // Cloudflare Stream thumbnail
      const cloudflareMatch = videoUrl.match(/videodelivery\.net\/([a-f0-9]+)/);
      if (cloudflareMatch) {
        const videoId = cloudflareMatch[1];
        // Cloudflare Stream thumbnail URL format
        return `https://videodelivery.net/${videoId}/thumbnails/thumbnail.jpg?time=1s&height=270`;
      }
      
      // YouTube - multiple patterns with better quality
      const youtubePatterns = [
        /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
        /(?:youtu\.be\/)([^&\s?]+)/,
        /(?:youtube\.com\/embed\/)([^&\s?]+)/,
        /(?:youtube\.com\/v\/)([^&\s?]+)/
      ];
      
      for (const pattern of youtubePatterns) {
        const match = videoUrl.match(pattern);
        if (match && match[1]) {
          // Use maxresdefault for highest quality
          return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
        }
      }
      
      // Vimeo thumbnail
      const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
      }
      
      // Dailymotion thumbnail
      const dailymotionMatch = videoUrl.match(/dailymotion\.com\/video\/([^_\s]+)/);
      if (dailymotionMatch) {
        return `https://www.dailymotion.com/thumbnail/video/${dailymotionMatch[1]}`;
      }
      
      // Default placeholder with branded color
      return placeholderImage("Play Video", 320, 180);
      
    } catch (error) {
      console.error('Error extracting thumbnail:', error);
      return placeholderImage("Video", 320, 180);
    }
  };

  // OPTIMIZED: Fetch all videos with PARALLEL requests and batching + CACHING
  useEffect(() => {
    const fetchAllVideos = async () => {
      // Check cache first
      const cachedData = getCachedVideos();
      if (cachedData) {
        setVideos(cachedData.videos);
        setCategoryMap(cachedData.categoryMap);
        setPartnerMap(cachedData.partnerMap);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      console.log("=== 🎥 FETCHING ALL VIDEOS (OPTIMIZED) ===");
      
      // Declare variables outside try block so finally can access them
      let tempVideos = [];
      let tempCategoryMap = {};
      let tempPartnerMap = {};
      
      try {
        // Step 1: Fetch all courses
        const formData = new URLSearchParams();
        formData.append('id', mentorId);
        
        const coursesResponse = await fetch(
          `${API_BASE}/Course_Controller/mentorcourse_details_getdata`,
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: formData,
          }
        );
        
        const coursesResult = await coursesResponse.json();
        
        if (coursesResult.Response === "OK" && Array.isArray(coursesResult.data)) {
          const courses = coursesResult.data;
          console.log(`✅ Found ${courses.length} courses`);
          
          setLoadingProgress({ current: 0, total: courses.length });
          
          // OPTIMIZED: Process courses in parallel batches
          const BATCH_SIZE = 5; // Process 5 courses at a time
          
          // Set loading to false after first batch starts
          let isFirstBatch = true;
          
          for (let i = 0; i < courses.length; i += BATCH_SIZE) {
            const batch = courses.slice(i, i + BATCH_SIZE);
            
            // Process batch in parallel
            const batchPromises = batch.map(async (course) => {
              try {
                // Fetch topics for this course
                const topicsResponse = await fetch(
                  `${API_BASE}/Course_Controller/get_topics_list`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: new URLSearchParams({ course_id: course.id }),
                  }
                );
                
                const topicsResult = await topicsResponse.json();
                
                if (topicsResult.Response === "OK" && topicsResult.data && topicsResult.data !== "FALSE") {
                  let topicsData = [];
                  
                  if (topicsResult.data.success && Array.isArray(topicsResult.data.data)) {
                    topicsData = topicsResult.data.data;
                  } else if (Array.isArray(topicsResult.data)) {
                    topicsData = topicsResult.data;
                  }
                  
                  // Fetch all topic contents in parallel
                  const contentPromises = topicsData.map(async (topic) => {
                    try {
                      const contentResponse = await fetch(
                        `${API_BASE}/Course_Controller/get_topic_by_id`,
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/x-www-form-urlencoded" },
                          body: new URLSearchParams({ id: topic.id }),
                        }
                      );
                      
                      const contentResult = await contentResponse.json();
                      
                      if (contentResult.Response === "OK" && contentResult.data) {
                        const topicData = contentResult.data;
                        
                        // Extract video_links
                        if (topicData.video_links && topicData.video_links.trim() !== '') {
                          const videoUrl = topicData.video_links;
                          
                          // IMPROVED: Use enhanced category detection
                          const category = getCategoryFromPartner(course.partner_name, course.course_name);
                          
                          // DEBUG: Log category assignments
                          console.log(`📁 [${category}] Course: "${course.course_name}" | Partner: "${course.partner_name}"`);
                          
                          // Create video object
                          return {
                            id: `${course.id}_${topic.id}_${Date.now()}_${Math.random()}`,
                            video_name: topicData.topic_name || "Video",
                            video_url: videoUrl,
                            topic_description: topicData.topic_description || "",
                            chapter: topicData.chapter || "",
                            sub_topic: topicData.sub_topic || "",
                            course_name: course.course_name,
                            partner_name: course.partner_name,
                            category: category,
                            source: course.partner_name,
                            video_img: extractThumbnail(videoUrl)
                          };
                        }
                      }
                    } catch (error) {
                      console.error(`Error fetching topic ${topic.id}:`, error);
                    }
                    return null;
                  });
                  
                  const videos = await Promise.all(contentPromises);
                  return videos.filter(v => v !== null);
                }
              } catch (error) {
                console.error(`Error processing course ${course.id}:`, error);
              }
              return [];
            });
            
            // Wait for batch to complete
            const batchResults = await Promise.all(batchPromises);
            const batchVideos = batchResults.flat();
            
            // Add to temp storage
            batchVideos.forEach(video => {
              tempVideos.push(video);
              tempCategoryMap[video.category] = (tempCategoryMap[video.category] || 0) + 1;
              tempPartnerMap[video.partner_name] = (tempPartnerMap[video.partner_name] || 0) + 1;
            });
            
            // Update progress and UI after each batch
            setLoadingProgress({ current: Math.min(i + BATCH_SIZE, courses.length), total: courses.length });
            setVideos([...tempVideos]);
            setCategoryMap({...tempCategoryMap});
            setPartnerMap({...tempPartnerMap});
            
            // Show videos immediately after first batch loads
            if (isFirstBatch && tempVideos.length > 0) {
              setLoading(false);
              isFirstBatch = false;
              console.log('✅ First batch loaded, showing videos!');
            }
          }
          
          // DEBUG: Log final category distribution
          console.log("📊 Final Category Distribution:", tempCategoryMap);
          console.log(`✅ Total videos loaded: ${tempVideos.length}`);
        }
      } catch (error) {
        console.error("❌ Error fetching videos:", error);
        setLoading(false);
      } finally {
        // Final cache update
        if (tempVideos.length > 0) {
          setCachedVideos({
            videos: tempVideos,
            categoryMap: tempCategoryMap,
            partnerMap: tempPartnerMap
          });
        }
      }
    };

    if (configLoaded) {
      fetchAllVideos();
    }
  }, [mentorId, configLoaded]);

  // Get available categories
  const availableCategories = Object.keys(categoryMap)
    .filter(cat => isCategoryVisible(cat))
    .sort((a, b) => {
      // Priority order
      const priority = {
        'K12': 1,
        'Mathematics': 2,
        'Vocational': 3,
        'Technology': 4,
        'UPSC': 5,
        'Medical': 6,
        'Management': 7,
        'Industry': 8,
        'Agriculture': 9,
        'Leadership': 10,
        'Other': 99
      };
      
      const priorityA = priority[a] || 50;
      const priorityB = priority[b] || 50;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      return a.localeCompare(b);
    });

  // Filter videos based on search and category
  const filteredVideos = videos.filter((video) => {
    const matchesSearch = video.video_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.topic_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.course_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === "All" || video.category === selectedCategory;
    const matchesPartner = selectedPartner === "All" || video.partner_name === selectedPartner;
    const matchesVisibility = isCategoryVisible(video.category);
    
    return matchesSearch && matchesCategory && matchesPartner && matchesVisibility;
  });

  // Handle video play
  const handlePlayVideo = (video) => {
    window.open(video.video_url, '_blank');
  };

  return (
    <div className="videos-page">
      {/* Recommended Videos (YouTube) */}
      <section className="rec-section">
        <div className="rec-head">
          <div className="rec-title-wrap">
            <FaYoutube className="rec-title-icon" />
            <div>
              <h2 className="rec-title">Recommended Videos</h2>
              <p className="rec-subtitle">
                {ytTopic ? <>Showing videos for <strong>{ytTopic}</strong></> : "Enter a topic to get videos"}
              </p>
            </div>
          </div>

          <form className="rec-form" onSubmit={handleTopicSubmit}>
            <input
              type="text"
              className="rec-input"
              placeholder="Enter a topic, e.g. Python basics, Photosynthesis..."
              value={ytTopicInput}
              onChange={(e) => setYtTopicInput(e.target.value)}
            />
            <button type="submit" className="rec-btn" disabled={ytLoading || !ytTopicInput.trim()}>
              {ytLoading ? "Searching..." : "Get Videos"}
            </button>
          </form>
        </div>

        {ytLoading && ytVideos.length === 0 ? (
          <div className="rec-row">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rec-card rec-skeleton">
                <div className="rec-thumb" />
                <div className="rec-sk-line" />
                <div className="rec-sk-line short" />
              </div>
            ))}
          </div>
        ) : ytError ? (
          <p className="rec-message">{ytError}</p>
        ) : (
          <div className={`rec-row ${ytLoading ? "is-loading" : ""}`}>
            {ytVideos.map((v) => (
              <button
                key={v.video_id}
                type="button"
                className="rec-card"
                onClick={() => setPlayingVideo(v)}
                title={v.title}
              >
                <div className="rec-thumb">
                  <img src={v.thumbnail} alt={v.title} loading="lazy" />
                  <span className="rec-play"><FaPlay /></span>
                </div>
                <h3 className="rec-card-title">{v.title}</h3>
                <p className="rec-card-channel">{v.channel}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* In-app YouTube Player */}
      {playingVideo && (
        <div className="rec-modal" onClick={() => setPlayingVideo(null)}>
          <div className="rec-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="rec-modal-main">
              <div className="rec-player">
                <iframe
                  key={playingVideo.video_id}
                  src={`https://www.youtube-nocookie.com/embed/${playingVideo.video_id}?autoplay=1&rel=0&modestbranding=1`}
                  title={playingVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="rec-modal-info">
                <h3>{playingVideo.title}</h3>
                <p>{playingVideo.channel}</p>
              </div>
            </div>

            <aside className="rec-upnext">
              <h4>More on "{ytTopic}"</h4>
              {ytVideos
                .filter((v) => v.video_id !== playingVideo.video_id)
                .map((v) => (
                  <button
                    key={v.video_id}
                    type="button"
                    className="rec-upnext-item"
                    onClick={() => setPlayingVideo(v)}
                  >
                    <img src={v.thumbnail} alt={v.title} loading="lazy" />
                    <div>
                      <span className="rec-upnext-title">{v.title}</span>
                      <span className="rec-upnext-channel">{v.channel}</span>
                    </div>
                  </button>
                ))}
            </aside>

            <button
              type="button"
              className="rec-close"
              onClick={() => setPlayingVideo(null)}
              aria-label="Close player"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="videos-header">
        <div className="header-content1">

          <div className="search-filter-container" ref={scrollContainerRef}>
            {/* Category Filters */}
            {selectedCategory === "All" && selectedPartner === "All" ? (
              <div className="core-filters">
                <button
                  onClick={() => setSelectedCategory("All")}
                  className="category-btn active"
                >
                  All ({videos.length})
                </button>
                {availableCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setVisibleCount(24);
                    }}
                    className="category-btn"
                  >
                    {category} ({categoryMap[category] || 0})
                  </button>
                ))}
              </div>
            ) : selectedCategory !== "All" ? (
              <div className="category-filters1">
                <button
                  onClick={() => {
                    setSelectedCategory("All");
                    setSelectedPartner("All");
                    setVisibleCount(24);
                  }}
                  className="back-btn2"
                >
                  <FaArrowLeft size={16} />
                </button>
                <button className="category-btn active">
                  {selectedCategory} ({categoryMap[selectedCategory] || 0})
                </button>
                
                {/* Show partners for this category */}
                {Object.keys(partnerMap)
                  .filter(partner => {
                    const hasVideos = videos.some(v => 
                      v.partner_name === partner && v.category === selectedCategory
                    );
                    return hasVideos;
                  })
                  .map(partner => (
                    <button
                      key={partner}
                      onClick={() => {
                        setSelectedPartner(partner);
                        setVisibleCount(24);
                      }}
                      className={`category-btn ${selectedPartner === partner ? 'active' : ''}`}
                    >
                      {partner}
                    </button>
                  ))
                }
              </div>
            ) : null}

            {/* Search Box */}
            <div className="search-box">
              <svg
                className="search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search videos..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setVisibleCount(24);
                }}
                className="search-input1"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="videos-grid-section">
        <div className="videos-container">
          {loading && videos.length === 0 ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading videos...</p>
              {loadingProgress.total > 0 && (
                <p className="loading-progress">
                  Processing {loadingProgress.current} of {loadingProgress.total} courses
                </p>
              )}
            </div>
          ) : videos.length === 0 && !loading ? (
            <div className="empty-state">
              <FaVideo size={64} color="#ccc" />
              <p>No videos found</p>
            </div>
          ) : (
            <>
              <div className="videos-grid">
                {filteredVideos.slice(0, visibleCount).map((video) => (
                  <div key={video.id} className="video-card-simple">
                    <div className="video-card-left">
                      <div className="video-card-header">
                        <div className="video-icon">
                          <FaPlay />
                        </div>
                        <span className="video-label">VIDEOS</span>
                      </div>
                      
                      <h3 className="video-card-title">{video.video_name}</h3>
                    </div>
                    
                    <button
                      onClick={() => handlePlayVideo(video)}
                      className="watch-button-simple"
                    >
                      Watch
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Show loading indicator while fetching more */}
              {loading && videos.length > 0 && (
                <div className="loading-more">
                  <div className="spinner-small"></div>
                  <p>Loading more videos... ({loadingProgress.current}/{loadingProgress.total} courses)</p>
                </div>
              )}
              
              {visibleCount < filteredVideos.length && (
                <button
                  className="load-more-btn2"
                  onClick={() => setVisibleCount(visibleCount + 24)}
                >
                  Load More ({filteredVideos.length - visibleCount} remaining)
                </button>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Video;